/**
 * Scout v0 — QA decision bar (P18, §1.2 stage 10).
 *
 * Per-lead action: accept the AI grade or override (A/B/C/D) with a REQUIRED
 * reason note on override. Keyboard-friendly: 1-4 = A-D, Enter = submit.
 *
 * OVERRIDE-REQUIRES-REASON: an override (finalGrade !== aiGrade) without a
 * non-empty note is blocked client-side AND backend-side (400). The UI must
 * not offer a submit path that bypasses the reason field on override — the
 * submit button is disabled until the note is filled.
 */

import { useEffect, useRef, useState } from 'react';
import type { ApiError } from '@/lib/api';
import { getErrorMessage } from '@/lib/queries';
import type { QaGrade, QaQueueLead } from '@/types/scout';
import { QaGradeBadge } from './QaGradeBadge';

const GRADES: QaGrade[] = ['A', 'B', 'C', 'D'];
const GRADE_HOTKEY: Record<string, QaGrade> = { '1': 'A', '2': 'B', '3': 'C', '4': 'D' };

interface QaDecisionBarProps {
  lead: QaQueueLead;
  aiGrade: QaGrade;
  isSubmitting: boolean;
  submitError: ApiError | null;
  onSubmit: (payload: { finalGrade: QaGrade; note: string }) => void;
}

// NOTE: the parent must key this component by lead rowRef (+ AI grade) so the
// selection/note state re-initializes when the active lead changes — see the
// QaQueuePanel render site.
export function QaDecisionBar({ lead, aiGrade, isSubmitting, submitError, onSubmit }: QaDecisionBarProps) {
  const [selectedGrade, setSelectedGrade] = useState<QaGrade>(aiGrade);
  const [note, setNote] = useState('');
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const isOverride = selectedGrade !== aiGrade;
  const noteRequired = isOverride;
  const noteFilled = note.trim().length > 0;
  const canSubmit = !isSubmitting && (!noteRequired || noteFilled);

  // Keyboard: 1-4 = grade hotkeys, Enter = submit (but not while typing in the note).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;

      if (e.key in GRADE_HOTKEY) {
        e.preventDefault();
        setSelectedGrade(GRADE_HOTKEY[e.key]!);
      } else if (e.key === 'Enter' && canSubmit) {
        e.preventDefault();
        onSubmit({ finalGrade: selectedGrade, note });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedGrade, note, canSubmit, onSubmit]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) {
      // Focus the note field when the override reason is missing.
      if (noteRequired && !noteFilled) noteRef.current?.focus();
      return;
    }
    onSubmit({ finalGrade: selectedGrade, note });
  }

  return (
    <section className="card" aria-labelledby="qa-decision-heading">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 id="qa-decision-heading" className="text-base font-semibold text-text">
            Decision
          </h2>
          <p className="text-xs text-muted mt-0.5 font-mono">{lead.rowRef}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted">AI grade</span>
          <QaGradeBadge grade={aiGrade} />
        </div>
      </div>

      <div className="mb-3">
        <p className="text-sm text-text font-medium">{lead.company.name}</p>
        <p className="text-xs text-muted">{lead.jobSignal.title}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Grade selection */}
        <fieldset>
          <legend className="text-xs text-muted mb-2">
            Final grade {isOverride && <span className="text-warning">(override)</span>}
          </legend>
          <div className="grid grid-cols-4 gap-2">
            {GRADES.map((grade) => {
              const isSel = grade === selectedGrade;
              const isOverrideGrade = grade !== aiGrade;
              return (
                <button
                  key={grade}
                  type="button"
                  onClick={() => setSelectedGrade(grade)}
                  aria-pressed={isSel}
                  className={`min-h-[40px] rounded-md border font-semibold transition-colors duration-fast ${
                    isSel
                      ? 'border-secondary bg-secondary text-white'
                      : isOverrideGrade
                        ? 'border-border text-text hover:bg-surface'
                        : 'border-border text-muted hover:bg-surface'
                  }`}
                  title={`Grade ${grade}${grade !== aiGrade ? ' (override)' : ''}`}
                >
                  {grade}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted mt-1.5">
            Hotkeys: <kbd className="font-mono">1</kbd>=A <kbd className="font-mono">2</kbd>=B{' '}
            <kbd className="font-mono">3</kbd>=C <kbd className="font-mono">4</kbd>=D
          </p>
        </fieldset>

        {/* Reason note — required on override */}
        <div>
          <label htmlFor="qa-note" className="text-xs text-muted mb-1.5 block">
            Reason note {noteRequired ? <span className="text-warning">*(required on override)</span> : '(optional)'}
          </label>
          <textarea
            id="qa-note"
            ref={noteRef}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="input"
            placeholder={
              noteRequired
                ? 'Explain the grade change (required for override)'
                : 'Add a note (optional)'
            }
            aria-required={noteRequired}
            aria-invalid={noteRequired && !noteFilled}
          />
          {noteRequired && !noteFilled && (
            <p className="text-xs text-warning mt-1" role="alert">
              A reason is required when overriding the AI grade.
            </p>
          )}
        </div>

        {/* Submit error */}
        {submitError && (
          <p className="text-sm text-warning" role="alert">
            {getErrorMessage(submitError)}
          </p>
        )}

        {/* Submit */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted">
            {selectedGrade === 'A' || selectedGrade === 'B' ? 'A/B → accepted' : 'C/D → rejected'}
          </p>
          <button
            type="submit"
            disabled={!canSubmit}
            className="btn-primary min-h-[40px]"
          >
            {isSubmitting ? 'Submitting...' : `Submit ${selectedGrade}`}
          </button>
        </div>
      </form>
    </section>
  );
}